/**
 * Permission Constants and Role Definitions
 * Defines all available permissions and default permissions for each user level
 */

// User type definitions
export type UserType = 'staff' | 'manager' | 'company' | 'super_admin';

// All available permissions in the system
export const PERMISSIONS = {
  // Level 1: Staff permissions
  CLOCK_IN: 'clock-in',
  CLOCK_OUT: 'clock-out',
  VIEW_SCHEDULE: 'view-schedule',
  VIEW_OWN_TIMESHEET: 'view-own-timesheet',

  // Level 2: Manager permissions (includes all staff permissions)
  ASSIGN_JOBS: 'assign-jobs',
  REVIEW_TIME_ENTRIES: 'review-time-entries',
  APPROVE_TIME_ENTRIES: 'approve-time-entries',
  CONFIGURE_GRACE_PERIOD: 'configure-grace-period',
  EXPORT_REPORTS: 'export-reports',
  VIEW_TEAM_DATA: 'view-team-data',

  // Level 3: Company permissions (includes all manager permissions)
  MANAGE_MANAGERS: 'manage-managers',
  MANAGE_ALL_STAFF: 'manage-all-staff',
  VIEW_ALL_COMPANY_DATA: 'view-all-company-data',
  CONFIGURE_COMPANY_SETTINGS: 'configure-company-settings',
  MANAGE_COMPANY_PROFILE: 'manage-company-profile',

  // Level 4: Super Admin permissions
  MANAGE_COMPANIES: 'manage-companies',
  VIEW_SYSTEM_ANALYTICS: 'view-system-analytics',
  MANAGE_SUPER_ADMINS: 'manage-super-admins',
  ACCESS_ALL_DATA: 'access-all-data',
} as const;

// Permission type for TypeScript
export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

// Default permissions for each user level
export const ROLE_PERMISSIONS: Record<UserType, Permission[]> = {
  staff: [
    PERMISSIONS.CLOCK_IN,
    PERMISSIONS.CLOCK_OUT,
    PERMISSIONS.VIEW_SCHEDULE,
    PERMISSIONS.VIEW_OWN_TIMESHEET,
  ],

  manager: [
    // Includes all staff permissions
    PERMISSIONS.CLOCK_IN,
    PERMISSIONS.CLOCK_OUT,
    PERMISSIONS.VIEW_SCHEDULE,
    PERMISSIONS.VIEW_OWN_TIMESHEET,
    // Plus manager-specific permissions
    PERMISSIONS.ASSIGN_JOBS,
    PERMISSIONS.REVIEW_TIME_ENTRIES,
    PERMISSIONS.APPROVE_TIME_ENTRIES,
    PERMISSIONS.CONFIGURE_GRACE_PERIOD,
    PERMISSIONS.EXPORT_REPORTS,
    PERMISSIONS.VIEW_TEAM_DATA,
  ],

  company: [
    // Includes all manager permissions
    PERMISSIONS.CLOCK_IN,
    PERMISSIONS.CLOCK_OUT,
    PERMISSIONS.VIEW_SCHEDULE,
    PERMISSIONS.VIEW_OWN_TIMESHEET,
    PERMISSIONS.ASSIGN_JOBS,
    PERMISSIONS.REVIEW_TIME_ENTRIES,
    PERMISSIONS.APPROVE_TIME_ENTRIES,
    PERMISSIONS.CONFIGURE_GRACE_PERIOD,
    PERMISSIONS.EXPORT_REPORTS,
    PERMISSIONS.VIEW_TEAM_DATA,
    // Plus company-specific permissions
    PERMISSIONS.MANAGE_MANAGERS,
    PERMISSIONS.MANAGE_ALL_STAFF,
    PERMISSIONS.VIEW_ALL_COMPANY_DATA,
    PERMISSIONS.CONFIGURE_COMPANY_SETTINGS,
    PERMISSIONS.MANAGE_COMPANY_PROFILE,
  ],

  super_admin: Object.values(PERMISSIONS), // All permissions
};

// User level metadata
export const USER_LEVEL_INFO: Record<UserType, {
  level: number;
  displayName: string;
  description: string;
  icon: string;
}> = {
  staff: {
    level: 1,
    displayName: 'Staff',
    description: 'Clock in/out, view schedules, track work hours, and manage your time entries.',
    icon: '👷',
  },
  manager: {
    level: 2,
    displayName: 'Manager/Supervisor',
    description: 'Assign jobs to staff, review time entries, configure policies, and export reports.',
    icon: '👔',
  },
  company: {
    level: 3,
    displayName: 'Company Administrator',
    description: 'Oversee managers and staff, manage company settings, and access all company data.',
    icon: '🏢',
  },
  super_admin: {
    level: 4,
    displayName: 'SPECHS Super Admin',
    description: 'System-wide access to manage all companies, users, and system settings.',
    icon: '⚡',
  },
};

// Helper function to check if a user level is higher than another
export const isHigherLevel = (userType: UserType, compareType: UserType): boolean => {
  return USER_LEVEL_INFO[userType].level > USER_LEVEL_INFO[compareType].level;
};

// Helper function to check if a user can manage another user type
export const canManageUserType = (managerType: UserType, targetType: UserType): boolean => {
  // Super admin can manage everyone
  if (managerType === 'super_admin') return true;

  // Company can manage managers and staff
  if (managerType === 'company' && (targetType === 'manager' || targetType === 'staff')) {
    return true;
  }

  // Manager can manage staff only
  if (managerType === 'manager' && targetType === 'staff') {
    return true;
  }

  return false;
};

// Default settings for each user type
export const DEFAULT_USER_SETTINGS = {
  staff: {
    // Staff (day workers) don't have a default hourlyRate
    // Their hourly rate is set by the company/manager when assigning jobs
  },
  manager: {
    // Managers are salaried supervisors who manage day workers
    // They don't clock in/out, so no hourlyRate
    managedStaffIds: [] as string[], // Staff members (day workers) this manager oversees
  },
  company: {
    // Company settings apply to all staff managed by this company
    companySettings: {
      gracePeriodMinutes: 5,
    },
  },
  super_admin: {
    // SPECHS platform administrators
    // No special settings needed
  },
};
